import {
  LuLoader,
  LuRefreshCw,
  LuShieldCheck,
  LuInfo,
  LuShield,
} from "react-icons/lu";
import { Card, Button } from "@destroy/ui";
import { PageHeader } from "@/components/ui/page-header";
import { useDomainHealth } from "./_hooks";
import { DomainHealthCard, HealthSummaryCard } from "./_components";

/**
 * Console mirror of the legacy /admin/domain-health page from the
 * public app. Same hook, same components, served via /admin/domains/*
 * on the console worker. Lets operators see DNSBL listings, disposable
 * detection, MX + SPF validity, and last-checked timestamps.
 */
export function DomainHealthPage() {
  const {
    data,
    loading,
    checking,
    checkingAll,
    checkDomain,
    checkAllDomains,
  } = useDomainHealth();

  const blockedCount = data?.summary.blocked ?? 0;
  const warningCount = data?.summary.warning ?? 0;

  return (
    <>
      <PageHeader
        icon={LuShield}
        title="Domain Health"
        description="DNSBL, disposable, and DNS configuration checks for every public domain."
        actions={
          <Button
            onClick={checkAllDomains}
            disabled={checkingAll}
          >
            <LuRefreshCw className={`w-4 h-4 ${checkingAll ? "animate-spin" : ""}`} />
            {checkingAll ? "Checking all..." : "Check all"}
          </Button>
        }
      />

      <div className="p-8 space-y-6">
        {loading && (
          <div className="min-h-[40vh] flex flex-col items-center justify-center gap-4">
            <LuLoader className="w-6 h-6 text-[var(--color-text-muted)] animate-spin" />
            <p className="text-sm text-[var(--color-text-muted)]">
              Loading domain health...
            </p>
          </div>
        )}

        {!loading && data && <HealthSummaryCard summary={data.summary} />}

        {!loading && (blockedCount > 0 || warningCount > 0) && (
          <Card padding="md" className="bg-[var(--color-error)]/5 border-[var(--color-error)]/20">
            <div className="flex items-start gap-3">
              <LuShieldCheck className="w-5 h-5 text-[var(--color-error)] flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-[var(--color-text)] mb-1">
                  Action required
                </p>
                <p className="text-[var(--color-text-muted)]">
                  {blockedCount > 0 && (
                    <span className="text-[var(--color-error)]">
                      {blockedCount} domain{blockedCount !== 1 ? "s are" : " is"} blocked and should be replaced.{" "}
                    </span>
                  )}
                  {warningCount > 0 && (
                    <span className="text-[var(--color-warning)]">
                      {warningCount} domain{warningCount !== 1 ? "s have" : " has"} warnings.
                    </span>
                  )}
                </p>
              </div>
            </div>
          </Card>
        )}

        {!loading && (
          <Card padding="md" className="bg-[var(--color-primary)]/5 border-[var(--color-primary)]/20">
            <div className="flex items-start gap-3">
              <LuInfo className="w-5 h-5 text-[var(--color-primary)] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[var(--color-text-muted)]">
                <p className="font-medium text-[var(--color-text)] mb-1">What we check</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>
                    <strong>DNS Blacklists (DNSBL)</strong> - Spamhaus, Barracuda, SORBS, SpamCop
                  </li>
                  <li>
                    <strong>Disposable email lists</strong> - Kickbox, Debounce detection services
                  </li>
                  <li>
                    <strong>DNS configuration</strong> - MX records and SPF records
                  </li>
                </ul>
                <p className="mt-2 text-xs">
                  Domains with low scores should be disabled and replaced to maintain delivery quality.
                </p>
              </div>
            </div>
          </Card>
        )}

        {!loading && data && (
          <div className="space-y-3">
            <h2 className="text-sm font-medium text-[var(--color-text-muted)]">
              All domains ({data.domains.length})
            </h2>

            {data.domains.length === 0 ? (
              <Card padding="lg" className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[var(--color-surface)] flex items-center justify-center">
                  <LuShieldCheck className="w-6 h-6 text-[var(--color-text-muted)]" />
                </div>
                <p className="text-sm text-[var(--color-text-muted)]">
                  No public domains configured. Add some on the Domains page.
                </p>
              </Card>
            ) : (
              <div className="space-y-2">
                {data.domains
                  .slice()
                  .sort((a, b) => {
                    const order = { blocked: 0, warning: 1, healthy: 2, unknown: 3 };
                    if (a.status !== b.status) {
                      return order[a.status] - order[b.status];
                    }
                    return a.score - b.score;
                  })
                  .map((health) => (
                    <DomainHealthCard
                      key={health.domainId}
                      health={health}
                      onCheck={() => checkDomain(health.domainId)}
                      checking={checking === health.domainId}
                    />
                  ))}
              </div>
            )}
          </div>
        )}

        {data?.lastFullCheck && (
          <p className="text-xs text-[var(--color-text-subtle)] text-center">
            Last full check: {new Date(data.lastFullCheck).toLocaleString()}
          </p>
        )}
      </div>
    </>
  );
}
